/**
 * Returns a `(value) => Promise<string>` resolver that coalesces synchronous
 * calls into a single batched lookup per microtask. Used by the page factory
 * to satisfy `MultiRelationFieldDef.resolveLabel` and
 * `AsyncSelectFieldDef.resolveLabel` without issuing N requests per editor open.
 *
 * Behavior:
 *   - Cache hit returns `Promise.resolve(cachedLabel)`. Always async — callers
 *     `.then(...)` regardless.
 *   - Cache miss queues the value into a `pending` Map and schedules a microtask
 *     flush (if not already scheduled).
 *   - Flush copies pending and resets it BEFORE awaiting the lookup, so any
 *     re-entrant calls form the next batch (not this one).
 *   - If `deps.signal` is already aborted at flush time, all queued resolvers
 *     resolve to `'#${id}'` WITHOUT calling `lookupOptions` — no point firing a
 *     request with a known-dead signal.
 *   - On lookup throw (including signal-aborted mid-flight), all flushed-batch
 *     resolvers get `'#${id}'`. Pending was already cleared, so a future call
 *     retries. Batcher does NOT log — `createApiOptionsLookup` already logged
 *     the technical error (or skipped if AbortError).
 *
 * Cache lifetime is the closure (page-factory invocation). No TTL — accepted
 * tradeoff: if a label changes mid-page-life, the cached version is shown until
 * next page navigation.
 *
 * @param {import('../../contracts/forms.js').OptionsLookupFn} lookupOptions
 * @param {{ signal?: AbortSignal }} [deps]
 * @returns {(value: unknown) => Promise<string>}
 */
export function createBatchedResolveLabel(lookupOptions, deps) {
  /** @type {Map<string, string>} */
  const cache = new Map();
  /** @type {Map<string, Array<(label: string) => void>>} */
  let pending = new Map();
  let scheduled = false;

  function flush() {
    const batch = pending;
    pending = new Map();
    scheduled = false;

    const ids = [...batch.keys()];

    const resolveAllToPlaceholders = () => {
      for (const [id, resolvers] of batch) {
        const placeholder = `#${id}`;
        for (const r of resolvers) r(placeholder);
      }
    };

    if (deps?.signal?.aborted) {
      resolveAllToPlaceholders();
      return;
    }

    Promise.resolve()
      .then(() => lookupOptions(ids, { signal: deps?.signal }))
      .then(options => {
        for (const opt of options) {
          if (opt && typeof opt.label === 'string') {
            cache.set(opt.value, opt.label);
          }
        }
        for (const [id, resolvers] of batch) {
          const label = cache.get(id) ?? `#${id}`;
          for (const r of resolvers) r(label);
        }
      })
      .catch(() => {
        resolveAllToPlaceholders();
      });
  }

  return (value) => {
    const id = String(value);
    const cached = cache.get(id);
    if (cached !== undefined) {
      return Promise.resolve(cached);
    }
    return new Promise((resolve) => {
      const existing = pending.get(id);
      if (existing) {
        existing.push(resolve);
      } else {
        pending.set(id, [resolve]);
      }
      if (!scheduled) {
        scheduled = true;
        queueMicrotask(flush);
      }
    });
  };
}
